import z from "zod";
import { ManagerApprovalStatus, PDFTypes } from "./application.dto";
import { DistributionType } from "./ledger.dto";
import { T_MongoId } from "../types/_utils.types";

export const DEFAULT_CURRENCY: NonNullable<StrategyConfigV2["currency"]> = {
  sign: "$",
  iso_code: "USD",
  name: "US Dollar",
  words: "Dollars",
};

/**
 * Resolution type for the program.
 */
export enum ResolutionType {
  /** Debt negotiation strategy */
  NEGOTIATE = "negotiate",

  /** Debt settlement strategy */
  SETTLEMENT = "settlement",
}

/**
 * Configuration format used in admin panel.
 */
export enum StrategyFormat {
  /** Default system format */
  DEFAULT = "default",

  /** Customizable variables format */
  CUSTOM = "custom",

  /** Requires manager approval before execution */
  MANAGERS_APPROVAL = "managers_approval",
}

/**
 * Payment frequency for the program.
 */
export enum PaymentType {
  /** Single payment */
  ONE_TIME = "one-time",

  /** Recurring weekly payment */
  WEEKLY = "weekly",

  /** Recurring monthly payment */
  MONTHLY = "monthly",
}

/**
 * Strategy used to calculate target weekly contribution.
 */
export enum TargetWeeklyCalculationStrategy {
  /** Standard unified weekly calculation */
  FIXED = "fixed",

  /** Lower weekly target strategy */
  LOW_WEEKLY = "low_weekly",

  /** Apply bias first before calculating weekly */
  PRIOR_BIAS = "prior_bias",

  COMPACT = "compact",
}

/**
 * Program fee split format.
 */
export enum ProgramSplitFormat {
  /** No special split rules */
  UNIFIED = "unified",

  /** Program may stop early based on number of weeks */
  EARLY_STOPPING_WEEKS = "early_stopping_weeks",

  /** Program may stop early based on percentage completion */
  EARLY_STOPPING_PERCENTAGE = "early_stopping_percentage",

  /** Fee calculated as percentage of weekly payment */
  PERCENTAGE_OF_WEEKLY = "percentage_of_weekly",

  /** Fee calculated as percentage of total revenue */
  PERCENTAGE_OF_REVENUE = "percentage_of_revenue",
}

/**
 * Creditor split representation format.
 */
export enum CreditorSplitFormat {
  /** Amount based creditor allocation */
  AMOUNT = "amount",

  /** Percentage based creditor allocation */
  PERCENTAGE = "percentage",
}

/**
 * How creditor payments are processed.
 */
export enum CreditorPayFormat {
  /** Payment processed via FCDS */
  FCDS = "fcds",

  /** Payment sent via System */
  DIRECT = "direct",
}

export enum InterestCalculationStrategy {
  /**
   * Default strategy for interest, `program_fee` is the percentage
   */
  PERCENTAGE = "percentage_of_debt",
  /**
   * Fixed interest amount
   */
  FIXED = "fixed",
  /**
   * Fixed amount per loan
   */
  FIXED_PER_LOAN = "fixed_per_loan",
  /**
   * Fixed amount per lawsuit
   */
  FIXED_PER_LAWSUIT = "fixed_per_lawsuit",
}

export enum InitialPaymentPercentageOf {
  TOTAL_DEBT = "total_debt",
  TOTAL_INTEREST = "total_interest",
}

/**
 * Currency configuration.
 */
export type Currency = {
  /** Currency symbol used in UI (e.g. "$") */
  sign: string;

  /** ISO currency code (e.g. "USD") */
  iso_code: string;

  /** Full currency name */
  name: string;

  /** Currency name in words (used in documents) */
  words: string;
};

/**
 * Configuration for program fee splitting.
 */
export type ProgramSplit = {
  /** Determines how program payments are split */
  format: ProgramSplitFormat;

  /** Format-specific configuration data */
  meta?: any;

  /**
   * Number of weeks for initial fixed payments.
   * Only used for certain split formats.
   */
  initial_payment_timeline?: number;
};

/**
 * UI timeline display rule.
 */
export type DisplayTimeline = {
  /**
   * Maximum debt amount for this row.
   * Example: 100000 means up to $100K debt.
   */
  debt_cap: number;

  /** Number of weeks displayed for the program */
  weeks: number;
};

/**
 * Creditor payment configuration.
 */
export type CreditorSplit = {
  /**
   * Total creditor payback ratio.
   *
   * Examples:
   * - 1 = 100% repayment
   * - 0.68 = 68% repayment
   */
  total_payback: number;

  /**
   * Upfront creditor payment bias amount (if any).
   */
  initial_bias_amount: number;

  /**
   * Whether creditor allocation is based on
   * fixed amount or percentage.
   */
  format: CreditorSplitFormat;

  /**
   * Method used to pay creditors.
   */
  pay_format: CreditorPayFormat;
};

export namespace ExtraFee {
  export enum CalculationMode {
    FIXED = "fixed_amount",
    PER_LAWSUIT = "fixed_per_lawsuit",
    // PER_LOAN = "fixed_per_loan",
  }

  export enum ApplyCondition {
    WITH_INSURANCE = "has_insurance",
    NO_INSURANCE = "no_insurance",

    IS_INITIAL = "is_initial",
    NOT_INITIAL = "not_initial",

    HAS_LAWSUITS = "has_lawsuits",
    NO_LAWSUITS = "no_lawsuits",
  }

  export type Config = {
    mode: CalculationMode;
    applyIf: ApplyCondition[];
    value: number;
    meta?: any;
  };
}

export enum StrategyRuleKeys {
  MAX_TENURE = "total_weeks.max",
  MIN_TENURE = "total_weeks.min",
  MAX_PROGRAM_TENURE = "program_weeks.max",
  MIN_PROGRAM_TENURE = "program_weeks.min",
  MAX_WEEKLY_PAYMENT = "total_weekly_payment.max",
  MIN_WEEKLY_PAYMENT = "total_weekly_payment.min",
  MAX_INTEREST = "total_interest.max",
  MIN_INTEREST = "total_interest.min",
}

export type StrategyRules = {
  key: StrategyRuleKeys;
  value: number;
};

/**
 * Main strategy configuration object.
 */
export type StrategyConfigV2 = {
  /**
   * Unique identifier for the strategy
   */
  slug: string;

  /**
   * Title for this strategy config
   */
  key?: string;

  /**
   * Description shown in the admin panel UI.
   */
  description?: string;

  /**
   * Strategy category.
   */
  resolution_type: ResolutionType;

  /**
   * Configuration format type.
   */
  format: StrategyFormat;

  /**
   * Payment schedule type.
   */
  payment_type: PaymentType;

  /**
   * Currency configuration used by this strategy.
   */
  currency?: Currency;

  /**
   * How interest is calculated for an application
   *
   * @default InterestCalculationStrategy.PERCENTAGE - Percentage of total debt
   */
  interest_calculation_strategy: InterestCalculationStrategy;

  /**
   * Fixed interest amount, Required if interest_calculation_strategy is not of type percent
   */
  interest_amount?: number;

  /**
   * Relief fee percentage.
   *
   * @example 0.10 = 10%
   */
  program_fee: number;

  /**
   * Success fee percentage.
   *
   * @example 0.12 = 12%
   */
  success_fee: number;

  /**
   * Portion of program fee collected in the first week.
   */
  initial_fee: number;

  /**
   * Base on which initial_fee is calculated upon
   */
  initial_fee_base: InitialPaymentPercentageOf;

  /**
   * Additional program fee bias charged upfront.
   */
  initial_bias_amount: number;

  /**
   * If true, then bias gets added to total interest
   * @default false As the initial fee gets deducted from the program fee
   */
  is_bias_exclusive: boolean;

  /**
   * Multiplier used when dynamically adjusting weekly contributions.
   *
   * Example:
   * - 0 → Strategy II
   * - 0.25 → Strategy III (1.25x Strategy II)
   */
  dynamic_weekly_pushback: number;

  /**
   *
   * Used to get estimate value of the settled debt amount
   */
  fallback_display_timeline?: number;

  /**
   * Strategy used to compute weekly target contribution.
   */
  target_weekly_calculation_strategy: TargetWeeklyCalculationStrategy;

  /**
   * Program fee splitting configuration.
   */
  program_split: ProgramSplit;

  /**
   * Timeline rows shown in UI based on debt ranges.
   */
  display_timeline?: DisplayTimeline[];

  /**
   * Creditor payment configuration.
   */
  creditor_split: CreditorSplit;

  /**
   * PandaDoc Templates
   */
  template_id?: T_MongoId;

  /**
   * EJS file name
   */
  preview_file_name?: PDFTypes;

  /**
   * Additional extensible metadata.
   */
  meta?: Record<string, unknown>;
  /**
   * Extra Rules
   */
  rules?: StrategyRules[];

  service_fee?: ExtraFee.Config[];
  legal_fee?: ExtraFee.Config[];
};

type WeeklySplit = {
  variant: "fixed" | "percentage";
  value: number;
};

export type PercentageStrategyConfig = {
  /**
   * Additional program fee bias charged upfront.
   */
  initial_bias_amount: number;

  /**
   * Portion of program fee collected in the first week.
   */
  initial_fee: number;

  /**
   * Relief fee percentage.
   *
   * @example 0.10 = 10%
   */
  program_fee: number;

  /**
   * Success fee percentage.
   *
   * @example 0.12 = 12%
   */
  success_fee: number;

  program_split: WeeklySplit;

  creditor_split: WeeklySplit;

  /**
   * Currency configuration used by this strategy.
   */
  currency?: Currency;

  /**
   * PandaDoc Templates
   */
  template_id?: T_MongoId;

  /**
   * Additional extensible metadata.
   */
  meta?: Record<string, unknown>;
  service_fee?: ExtraFee.Config[];
  legal_fee?: ExtraFee.Config[];
};

export const extraFeeConfig = z.object({
  mode: z.nativeEnum(ExtraFee.CalculationMode),
  applyIf: z.array(z.nativeEnum(ExtraFee.ApplyCondition)).default([]),
  value: z.number(),
  meta: z.record(z.string(), z.unknown()).default({}),
});

export const strategyConfigZodSchema = z.object({
  slug: z.string(),
  key: z.string(),
  description: z.string().default(""),
  resolution_type: z.nativeEnum(ResolutionType),
  format: z.nativeEnum(StrategyFormat),
  payment_type: z.nativeEnum(PaymentType),
  currency: z
    .object({
      sign: z.string(),
      iso_code: z.string(),
      name: z.string(),
      words: z.string(),
    })
    .default(DEFAULT_CURRENCY),
  interest_calculation_strategy: z
    .nativeEnum(InterestCalculationStrategy)
    .default(InterestCalculationStrategy.PERCENTAGE),
  interest_amount: z.number().min(0).optional(),
  program_fee: z.number().min(0).max(1),
  success_fee: z.number().min(0).max(1),
  initial_fee: z.number().min(0).max(1),
  initial_fee_base: z.nativeEnum(InitialPaymentPercentageOf).default(InitialPaymentPercentageOf.TOTAL_DEBT),
  initial_bias_amount: z.number().min(0),
  is_bias_exclusive: z.boolean().default(false),
  dynamic_weekly_pushback: z.number().min(-1).max(1),
  target_weekly_calculation_strategy: z.nativeEnum(TargetWeeklyCalculationStrategy),
  fallback_display_timeline: z.number().min(0).max(1).optional(),
  program_split: z.object({
    format: z.nativeEnum(ProgramSplitFormat),
    meta: z.record(z.string(), z.unknown()).default({}),
    initial_payment_timeline: z.number().min(0).default(0),
  }),
  display_timeline: z
    .array(
      z.object({
        debt_cap: z.number().min(0),
        weeks: z.number().min(0),
      }),
    )
    .default([]),
  creditor_split: z.object({
    total_payback: z.number().min(0).max(1),
    initial_bias_amount: z.number().min(0),
    format: z.nativeEnum(CreditorSplitFormat),
    pay_format: z.nativeEnum(CreditorPayFormat),
  }),
  meta: z.record(z.string(), z.unknown()).default({}),
  template_id: z.string().optional(),
  preview_file_name: z.nativeEnum(PDFTypes).optional(),
  rules: z
    .array(
      z.object({
        key: z.nativeEnum(StrategyRuleKeys),
        value: z.number(),
      }),
    )
    .default([]),
  service_fee: z.array(extraFeeConfig).optional(),
  legal_fee: z.array(extraFeeConfig).optional(),
});

export const percentageStrategyConfigZodSchema = z.object({
  initial_bias_amount: z.number().min(0).default(0),
  program_fee: z.number().min(0).max(1),
  success_fee: z.number().min(0).max(1),
  initial_fee: z.number().min(0).max(1),
  currency: z
    .object({
      sign: z.string(),
      iso_code: z.string(),
      name: z.string(),
      words: z.string(),
    })
    .default(DEFAULT_CURRENCY),
  program_split: z.object({
    variant: z.enum(["fixed", "percentage"]),
    value: z.coerce.number().min(0),
  }),
  creditor_split: z.object({
    variant: z.enum(["fixed", "percentage"]),
    value: z.coerce.number().min(0),
  }),
  meta: z.record(z.string(), z.unknown()).default({}),
  template_id: z.string().optional(),
  service_fee: z.array(extraFeeConfig).optional(),
  legal_fee: z.array(extraFeeConfig).optional(),
});

export const addStrategyConfigZodSchema = strategyConfigZodSchema
  .omit({ slug: true })
  .extend({ slug: z.string().optional() });

export const updateStrategyConfigZodSchema = strategyConfigZodSchema.omit({ slug: true }).partial();

export const defaultStrategyValue: Omit<StrategyConfigV2, "slug"> & { dynamic_pushback_toggle: boolean } = {
  key: "",
  description: "",
  resolution_type: ResolutionType.NEGOTIATE,
  format: StrategyFormat.DEFAULT,
  payment_type: PaymentType.WEEKLY,
  currency: DEFAULT_CURRENCY,
  interest_calculation_strategy: InterestCalculationStrategy.PERCENTAGE,
  interest_amount: 0,
  program_fee: 0,
  success_fee: 0,
  initial_fee: 0,
  initial_fee_base: InitialPaymentPercentageOf.TOTAL_DEBT,
  initial_bias_amount: 0,
  is_bias_exclusive: false,
  dynamic_weekly_pushback: 0,
  dynamic_pushback_toggle: true,
  fallback_display_timeline: 0.8,
  target_weekly_calculation_strategy: TargetWeeklyCalculationStrategy.PRIOR_BIAS,
  display_timeline: [
    {
      debt_cap: 50000,
      weeks: 20,
    },
    {
      debt_cap: 125000,
      weeks: 40,
    },
    {
      debt_cap: 225000,
      weeks: 60,
    },
    {
      debt_cap: 325000,
      weeks: 80,
    },
    {
      debt_cap: 525000,
      weeks: 100,
    },
  ],
  program_split: {
    format: ProgramSplitFormat.PERCENTAGE_OF_WEEKLY,
    initial_payment_timeline: 0,
    meta: {
      mode: "variable",
      fixed_weeks: 50,
      percentage: 40,
      steps: [
        {
          debt_cap: 50000,
          weeks: 20,
        },
        {
          debt_cap: 125000,
          weeks: 40,
        },
        {
          debt_cap: 225000,
          weeks: 60,
        },
        {
          debt_cap: 325000,
          weeks: 80,
        },
        {
          debt_cap: 525000,
          weeks: 100,
        },
      ],
    },
  },
  creditor_split: {
    total_payback: 1,
    initial_bias_amount: 0,
    format: CreditorSplitFormat.AMOUNT,
    pay_format: CreditorPayFormat.FCDS,
  },
  meta: {},
};

/**
 * Application (Loan) context for strategy generation.
 */
export type StrategyGenerationClientContext = {
  appKey: string;
  /** Total debt amount */
  total_debt: number;
  /** Number of active lawsuits */
  lawsuits: number;
  /** Number of active loans */
  loans: number;
  /** Weekly payment budget (if any) */
  weekly_budget?: number;
  /** Total weeks to resolve (client provided) */
  total_weeks?: number;
  /** Legal cost with no lawsuits applied */
  no_lawsuit_pay: {
    one_time: number;
    monthly: number;
  };
  /** Legal cost with lawsuits applied */
  with_lawsuit_pay: {
    one_time: number;
    monthly: number;
  };
  /** Weekly service fee */
  service_weekly: number;
  /** Current weekly payment amount (if any) */
  current_weekly_pay?: number;
  /** Monthly revenue of debtor */
  monthly_revenue?: number;
  /** Monthly profit margin */
  monthly_profit?: number;
  /** Weekly profit margin */
  weekly_profit?: number;
  /** Percentage of receivable */
  profit_percentage?: number;
  /** Current status of manager approval */
  manager_approval_status: ManagerApprovalStatus;
};

export type PercentageStrategyContext = {
  /** Show percentage in UI if not fixed */
  split_type: DistributionType;
  /** Creditor total for ledger */
  totalDebt: number;
  /** Total payable without initial fee and weekly extra fee */
  totalPayableRemaining: number;
  /** Initial interest, service and legal payment */
  initialPay: number;
  /** Initial interest payment */
  initialInterestPay: number;
  /** Initial and weekly interest total */
  totalInterest: number;
  /** This gets distributed across weeks */
  remainingInterest: number;
  /** Maximum weekly budget */
  weekly_budget: number;
  /** Percentage of weekly receivables */
  profit_percentage: number;
  /** Weekly overflow amount */
  weekly_adjust: number;
  /** Weekly Payment */
  weekly_total: number;
  weekly_interest: number;
  weekly_creditor: number;
  weekly_service: number;
  weekly_legal: number;
  initial_legal: number;
  program_percentage?: number;
  creditor_percentage?: number;

  expectedProgramTenure: number;
  expectedCreditorTenure: number;
};

export type StrategyResponse = {
  key: string;
  slug: string;
  selector_key: string;
  title: string;
  sub_title: string;
  features: string[];
  main_payment_title: string;
  main_payment_amount: number;
  sub_payment_title: string;
  sub_payment_amount: number;
  footer_text?: string;
  overline: string;
  highlighted?: boolean;
  total_weeks: number;
  program_tenure: number;
  ledger_type: DistributionType;
  unavailable?: boolean;
  hide?: string[];
  resolution_type: ResolutionType;
  format: StrategyFormat;
  version?: number;
  payment_type: PaymentType;
  interest_calculation_strategy: InterestCalculationStrategy;
  target_weekly_calculation_strategy: TargetWeeklyCalculationStrategy;
  program_split_format: ProgramSplitFormat;
  creditor_payment_format: CreditorPayFormat;
  display_timeline: number;
  common: {
    lawsuit_initial: number;
    lawsuit_weekly: number;
    service_fee_weekly: number;
    service_fee_initial?: number;
    interest: number;
    interest_rate: number;
    /** Total initial payment */
    initial_payment: number;
    initial_program_fee: number;
    weekly_payments: number;
    creditor_payable: number;
    total_payable: number;
    total_debt: number;
    strategy_clause: number;
    is_custom_divided: boolean;
    is_managers_approval: boolean;
    selector_idx: number;
    split_rows: {
      creditor_start: number;
      creditor_end: number;
      creditor_max_weekly: number[];
      program_start: number;
      program_end: number;
      program_weekly: number;
      initial_bias_amount: number;
    };
    /** If client decides to pay initial amount over more than 1 week */
    is_initial_split: boolean;
    /** If initial split, then what is the upfront payment amount */
    initial_amount?: number;
    /** Number of weeks, the initial split is spread */
    initial_split?: number;
    split_at: number;
  };
};

export type StrategyResponseUiFields = keyof StrategyResponse &
  (
    | "key"
    | "title"
    | "sub_title"
    | "features"
    | "main_payment_title"
    | "main_payment_amount"
    | "sub_payment_title"
    | "sub_payment_amount"
    | "footer_text"
    | "overline"
    | "highlighted"
    | "hide"
  );

export type PaymentTableRow = {
  week: number;
  payment: number;
  program_fee: number;
  service_fee: number;
  legal_fee: number;
  creditor: number;
};

export type PaymentTable = {
  headers: string[];
  first_row: PaymentTableRow;
  repeat_rows: {
    row: PaymentTableRow;
    count: number;
  }[];
  totals: PaymentTableRow;
  weeks: number;
  resolution: "month" | "week";
  lawsuit: boolean;
};

export type GuardRails = {
  max_custom_weeks: number;
  min_custom_amount: number;
};

export type SignatureFieldDto = {
  type: string;
  field_id: string;
  layout: {
    position: {
      anchor_point: string;
      offset_x: number;
      offset_y: number;
    };
    page: number;
    style: {
      width: number;
      height: number;
    };
  };
};

export type MapItem = {
  fn: string;
  type: "value" | "image";
};

export type PandaDocMapping = {
  _id: T_MongoId;
  org_code: string;
  templateId: string;
  map: Record<string, MapItem>;
  title: string;
  signature_fields?: SignatureFieldDto[];
};
